import { Briefcase, Globe, MapPin, Tags, UserCheck } from 'lucide-react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'
import { FilterOptionList, type FilterOption } from '@/components/filters/filter-menu'
import { OPEN_TO_WORK_BADGE_LABEL } from '@/components/profile/open-to-work-badge'
import {
  facetsToSkillFilterOptions,
  selectedSkillKeysFromLabels,
  skillLabelsFromKeys,
  SkillFilterMenuContent,
} from '@/components/filters/skill-filter-menu-content'
import { formatMultiValueChipDisplayFromLabels } from '@/lib/list-filters/list-filter-params'
import {
  citySelectionKey,
  normalizeCompanyKey,
  normalizeCountryKey,
  parseCitySelectionKey,
  type TalentBrowseFilters,
  type TalentCitySelection,
  type TalentFacet,
} from '@/lib/talent-browse-filters'
import { buildUnifiedSingleFilterDef } from '@/lib/list-filters/build-unified-filter-def'
import { isProfileSectionEnabled } from '@/constants/profile-feature'

/** Facet catalog for people browse (recruiter talent, job applicants tracking, …). */
export type PeopleBrowseFilterCtx = {
  skillFacets: TalentFacet[]
  countryFacets: TalentFacet[]
  cityFacets: TalentFacet[]
  companyFacets: TalentFacet[]
  /** Legacy `ListFilterBar` only — when `false`, hides geo/skill chips. Omit for `UnifiedListFilterBar`. */
  showFilters?: boolean
}

export type RecruiterTalentFilterCtx = PeopleBrowseFilterCtx & {
  countryFilterOptions: FilterOption[]
  cityFilterOptions: FilterOption[]
  selectedCountryKeys: string[]
  selectedCityKeys: string[]
  onCountryFilterChange: (keys: string[]) => void
  onCityFilterChange: (keys: string[]) => void
  showFilters: boolean
}

function companyLabelsFromKeys(
  keys: string[],
  facets: TalentFacet[],
  fallback: string[],
): string[] {
  const facetByKey = new Map(facets.map((facet) => [facet.key, facet.label]))
  return keys.map((key, index) => facetByKey.get(key) ?? fallback[index] ?? key)
}

function isRecruiterLegacyCtx(ctx: PeopleBrowseFilterCtx): ctx is RecruiterTalentFilterCtx {
  return 'countryFilterOptions' in ctx
}

export function getPeopleBrowseFilterDefinitions<
  TFilters extends TalentBrowseFilters = TalentBrowseFilters,
  TCtx extends PeopleBrowseFilterCtx = PeopleBrowseFilterCtx,
>(): FilterDefinition<TFilters, TCtx>[] {
  return [
    {
      key: 'skills',
      label: 'Skills',
      icon: Tags,
      displayValue: (filters) => formatMultiValueChipDisplayFromLabels(filters.selectedSkills),
      chipValueTitle: (filters) =>
        filters.selectedSkills.length > 0 ? filters.selectedSkills.join(', ') : undefined,
      isNonDefault: (filters) => filters.selectedSkills.length > 0,
      reset: (filters) =>
        ({
          ...filters,
          selectedSkills: [],
          skillMatchMode: 'and',
        }) as TFilters,
      renderPopover: ({ filters, onChange, ctx }) => (
        <SkillFilterMenuContent
          facets={ctx.skillFacets}
          selectedSkills={filters.selectedSkills}
          skillMatchMode={filters.skillMatchMode}
          onSelectedSkillsChange={(selectedSkills) => onChange({ ...filters, selectedSkills })}
          onSkillMatchModeChange={(skillMatchMode) => onChange({ ...filters, skillMatchMode })}
          showMatchMode={false}
        />
      ),
      hidden: (ctx) => ctx.showFilters === false,
      unified: {
        selection: 'multi',
        optionsSource: 'faceted',
        typeaheadValues: false,
        searchPlaceholder: 'Search skills…',
        emptyLabel: 'No skills found',
        getOptions: (ctx) => facetsToSkillFilterOptions(ctx.skillFacets),
        getSelectedValues: (filters) => selectedSkillKeysFromLabels(filters.selectedSkills),
        setSelectedValues: (filters, values, ctx) => ({
          ...filters,
          selectedSkills: skillLabelsFromKeys(values, ctx.skillFacets, filters.selectedSkills),
        }),
      },
    },
    {
      key: 'countries',
      label: 'Countries',
      icon: Globe,
      displayValue: (filters) => formatMultiValueChipDisplayFromLabels(filters.selectedCountries),
      chipValueTitle: (filters) =>
        filters.selectedCountries.length > 0 ? filters.selectedCountries.join(', ') : undefined,
      isNonDefault: (filters) => filters.selectedCountries.length > 0,
      reset: (filters) =>
        ({
          ...filters,
          selectedCountries: [],
          selectedCities: [],
        }) as TFilters,
      renderPopover: ({ ctx }) =>
        isRecruiterLegacyCtx(ctx) ? (
          <FilterOptionList
            type="multi"
            options={ctx.countryFilterOptions}
            value={ctx.selectedCountryKeys}
            onValueChange={ctx.onCountryFilterChange}
            searchPlaceholder="Search countries…"
            emptyLabel="No countries found"
          />
        ) : null,
      hidden: (ctx) =>
        ctx.showFilters === false ||
        (ctx.countryFacets.length === 0 &&
          isRecruiterLegacyCtx(ctx) &&
          ctx.countryFilterOptions.length === 0),
      unified: {
        selection: 'multi',
        optionsSource: 'faceted',
        typeaheadValues: false,
        searchPlaceholder: 'Search countries…',
        emptyLabel: 'No countries found',
        getOptions: (ctx) =>
          ctx.countryFacets.map((facet) => ({
            value: facet.key,
            label: facet.label,
            count: facet.count,
          })),
        getSelectedValues: (filters) => filters.selectedCountries.map(normalizeCountryKey),
        setSelectedValues: (filters, values, ctx) =>
          recruiterCountryFilterChange(filters, values, ctx.countryFacets),
      },
    },
    {
      key: 'cities',
      label: 'Cities',
      icon: MapPin,
      displayValue: (filters) =>
        formatMultiValueChipDisplayFromLabels(filters.selectedCities.map((selection) => selection.city)),
      chipValueTitle: (filters) =>
        filters.selectedCities.length > 0
          ? filters.selectedCities.map((selection) => selection.city).join(', ')
          : undefined,
      isNonDefault: (filters) => filters.selectedCities.length > 0,
      reset: (filters) => ({ ...filters, selectedCities: [] }) as TFilters,
      renderPopover: ({ ctx }) =>
        isRecruiterLegacyCtx(ctx) ? (
          <FilterOptionList
            type="multi"
            options={ctx.cityFilterOptions}
            value={ctx.selectedCityKeys}
            onValueChange={ctx.onCityFilterChange}
            searchPlaceholder="Search cities…"
            emptyLabel="No cities found"
          />
        ) : null,
      hidden: (ctx) =>
        ctx.showFilters === false ||
        (ctx.cityFacets.length === 0 &&
          isRecruiterLegacyCtx(ctx) &&
          ctx.cityFilterOptions.length === 0),
      unified: {
        selection: 'multi',
        optionsSource: 'faceted',
        typeaheadValues: false,
        searchPlaceholder: 'Search cities…',
        emptyLabel: 'No cities found',
        getOptions: (ctx) =>
          ctx.cityFacets.map((facet) => ({
            value: facet.key,
            label: facet.label,
            count: facet.count,
          })),
        getSelectedValues: (filters) =>
          filters.selectedCities.map((selection) => citySelectionKey(selection.city, selection.country)),
        setSelectedValues: (filters, values) => recruiterCityFilterChange(filters, values),
      },
    },
    {
      key: 'companies',
      label: 'Company',
      icon: Briefcase,
      displayValue: (filters) => formatMultiValueChipDisplayFromLabels(filters.selectedCompanies),
      chipValueTitle: (filters) =>
        filters.selectedCompanies.length > 0 ? filters.selectedCompanies.join(', ') : undefined,
      isNonDefault: (filters) => filters.selectedCompanies.length > 0,
      reset: (filters) => ({ ...filters, selectedCompanies: [] }) as TFilters,
      hidden: (ctx) => ctx.showFilters === false || ctx.companyFacets.length === 0,
      unified: {
        selection: 'multi',
        optionsSource: 'faceted',
        typeaheadValues: false,
        searchPlaceholder: 'Search companies…',
        emptyLabel: 'No companies found',
        getOptions: (ctx) =>
          ctx.companyFacets.map((facet) => ({
            value: facet.key,
            label: facet.label,
            count: facet.count,
          })),
        getSelectedValues: (filters) =>
          filters.selectedCompanies.map((company) => normalizeCompanyKey(company)),
        setSelectedValues: (filters, values, ctx) => ({
          ...filters,
          selectedCompanies: companyLabelsFromKeys(
            values,
            ctx.companyFacets,
            filters.selectedCompanies,
          ),
        }),
      },
    },
  ]
}

export function getRecruiterTalentFilterDefinitions(): FilterDefinition<
  TalentBrowseFilters,
  RecruiterTalentFilterCtx
>[] {
  const definitions = getPeopleBrowseFilterDefinitions<TalentBrowseFilters, RecruiterTalentFilterCtx>()
  if (!isProfileSectionEnabled('openToWork', 'recruiter')) {
    return definitions
  }
  return [
    ...definitions,
    buildUnifiedSingleFilterDef<TalentBrowseFilters, RecruiterTalentFilterCtx>({
      key: 'open_to_work',
      label: OPEN_TO_WORK_BADGE_LABEL,
      icon: UserCheck,
      options: [
        { value: 'open', label: OPEN_TO_WORK_BADGE_LABEL },
      ],
      labels: { open: OPEN_TO_WORK_BADGE_LABEL },
      getValue: (filters) => (filters.openToWorkOnly ? 'open' : ''),
      setValue: (filters, value) => ({ ...filters, openToWorkOnly: value === 'open' }),
      emptyLabel: 'Anyone',
    }),
  ]
}

type LocationBrowseFilters = Pick<TalentBrowseFilters, 'selectedCountries' | 'selectedCities'>

export function recruiterCountryFilterChange<T extends LocationBrowseFilters>(
  filters: T,
  keys: string[],
  countryFacets: TalentFacet[],
): T {
  const facetByKey = new Map(countryFacets.map((facet) => [facet.key, facet.label]))
  const selectedCountries = keys.map((key) => facetByKey.get(key) ?? key)
  const keySet = new Set(keys)
  const selectedCities = filters.selectedCities.filter((selection) =>
    keySet.has(normalizeCountryKey(selection.country)),
  )
  return { ...filters, selectedCountries, selectedCities }
}

export function recruiterCityFilterChange<T extends LocationBrowseFilters>(
  filters: T,
  keys: string[],
): T {
  const selectedCities = keys
    .map((key) => parseCitySelectionKey(key))
    .filter((selection): selection is TalentCitySelection => selection !== null)

  const countryOnly = filters.selectedCountries.filter(
    (country) =>
      !filters.selectedCities.some(
        (selection) => normalizeCountryKey(selection.country) === normalizeCountryKey(country),
      ),
  )
  const countriesFromCities = selectedCities.map((selection) => selection.country)
  const selectedCountries = [...new Set([...countryOnly, ...countriesFromCities])]

  return { ...filters, selectedCities, selectedCountries }
}
